import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";

const AMENITIES = [
  { label: "Kreedo Lab", img: "/amenity-kreedo-lab.jpg" },
  { label: "Science Lab", img: "/amenity-science-lab.jpg" },
  { label: "Playground", img: "/amenity-playground.jpg" },
  { label: "Smart Classroom", img: "/amenity-smart-classroom.jpg" },
  { label: "Auditorium", img: "/amenity-auditorium.jpg" },
];

const UNIFORMS = [
  { label: "Nursery", img: "/uniform-nursery.png" },
  { label: "Nursery PT", img: "/uniform-nursery-pt.png" },
  { label: "LKG", img: "/uniform-lkg.png" },
  { label: "LKG PT", img: "/uniform-lkg-pt.png" },
  { label: "UKG", img: "/uniform-ukg.png" },
  { label: "UKG PT", img: "/uniform-ukg-pt.png" },
  { label: "1st-5th", img: "/uniform-1-5.png", ptNote: true },
  { label: "6th-8th", img: "/uniform-6-8.png", ptNote: true },
  { label: "9th-10th", img: "/uniform-9-10.png", ptNote: true },
];

const scrollToId = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(id, { duration: 1.3, offset: -72 });
  } else {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

const ImgFallback = ({ src, note }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0E2C4E] px-4 text-center">
    <span className="block h-12 w-12 overflow-hidden rounded-full opacity-50 ring-1 ring-white/20">
      <img
        src="/logo.png"
        alt=""
        className="h-full w-full scale-[2.9] object-cover object-[49%_49%]"
      />
    </span>
    <p className="text-[9px] font-medium uppercase tracking-[0.22em] text-[#D6EBEE]/50">
      [ {note} ]
      <br />
      <span className="normal-case tracking-normal">{src}</span>
    </p>
  </div>
);

const AmenityCard = ({ label, img, testId }) => {
  const [active, setActive] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <button
      type="button"
      onClick={() => setActive((v) => !v)}
      data-testid={testId}
      aria-pressed={active}
      className={`group relative w-[68%] shrink-0 snap-center overflow-hidden rounded-2xl text-left ring-1 ring-[#143A66]/10 transition-shadow duration-500 hover:shadow-[0_24px_50px_-20px_rgba(10,38,71,0.4)] sm:w-[42%] lg:w-auto ${
        active ? "is-active" : ""
      }`}
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#0E2C4E]">
        <ImgFallback src={img} note="Photo placeholder" />
        {!failed && (
          <img
            src={img}
            alt={label}
            loading="lazy"
            onError={() => setFailed(true)}
            className="absolute inset-0 h-full w-full object-cover saturate-[0.65] brightness-[0.82] transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.06] group-hover:saturate-100 group-hover:brightness-100 group-[.is-active]:scale-[1.06] group-[.is-active]:saturate-100 group-[.is-active]:brightness-100"
          />
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A2647]/85 to-transparent px-4 pb-4 pt-12">
          <p className="translate-y-1.5 font-display text-base font-semibold text-white opacity-80 transition-[transform,opacity] duration-500 group-hover:translate-y-0 group-hover:opacity-100 group-[.is-active]:translate-y-0 group-[.is-active]:opacity-100">
            {label}
          </p>
        </div>
      </div>
    </button>
  );
};

const UniformImage = ({ src, alt, testId }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div className="relative flex h-[280px] w-full items-center justify-center md:h-[380px]">
      {failed ? (
        <ImgFallback src={src} note="Uniform cutout placeholder" />
      ) : (
        <img
          src={src}
          alt={alt}
          data-testid={testId}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-auto max-w-full object-contain"
        />
      )}
    </div>
  );
};

const UniformCarousel = () => {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const goTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    setIdx(Math.round(track.scrollLeft / track.clientWidth));
  };

  const arrowCls =
    "absolute top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-[#143A66]/15 bg-white text-[#143A66] shadow-md transition-colors duration-300 hover:border-[#F47221] hover:text-[#F47221] disabled:opacity-30 disabled:hover:border-[#143A66]/15 disabled:hover:text-[#143A66]";

  return (
    <div>
      <div
        className="flex flex-wrap justify-center gap-2"
        role="tablist"
        aria-label="Uniform categories"
      >
        {UNIFORMS.map((u, i) => (
          <button
            key={u.label}
            type="button"
            role="tab"
            aria-selected={idx === i}
            onClick={() => goTo(i)}
            data-testid={`uniform-tab-${i}`}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors duration-300 ${
              idx === i
                ? "border-[#143A66] bg-[#143A66] text-white"
                : "border-[#143A66]/20 bg-white/60 text-[#143A66]/70 hover:border-[#F47221] hover:text-[#F47221]"
            }`}
          >
            {u.label}
          </button>
        ))}
      </div>

      <div className="relative mt-8">
        <div
          ref={trackRef}
          onScroll={onScroll}
          data-lenis-prevent
          data-testid="uniform-track"
          className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto"
        >
          {UNIFORMS.map((u, i) => (
            <div
              key={u.label}
              className="flex w-full shrink-0 snap-center flex-col items-center px-4"
              data-testid={`uniform-slide-${i}`}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#F47221]">
                {u.label} — Uniform
              </p>
              <div className="mt-4 w-full max-w-md rounded-2xl border border-[#143A66]/10 bg-white/80 p-6 shadow-sm md:p-8">
                <UniformImage
                  src={u.img}
                  alt={`${u.label} uniform`}
                  testId={`uniform-image-${i}`}
                />
              </div>
              {u.ptNote && (
                <p className="mt-4 max-w-sm text-center text-xs leading-relaxed text-[#143A66]/60">
                  PT uniform colors vary by house, so no single photo is shown
                  here.
                </p>
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => goTo(Math.max(0, idx - 1))}
          disabled={idx === 0}
          data-testid="uniform-prev"
          aria-label="Previous uniform category"
          className={`${arrowCls} left-0 md:-left-4`}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => goTo(Math.min(UNIFORMS.length - 1, idx + 1))}
          disabled={idx === UNIFORMS.length - 1}
          data-testid="uniform-next"
          aria-label="Next uniform category"
          className={`${arrowCls} right-0 md:-right-4`}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default function CampusLife() {
  return (
    <main className="relative">
      <Navbar />

      <PageHero
        eyebrow="Val-Hue High School & Junior College"
        title="Campus Life"
        titleTestId="campus-title"
        heroTestId="campus-hero"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 backdrop-blur-md"
          data-testid="campus-jump-card"
        >
          <span className="px-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D6EBEE]/70">
            On this page
          </span>
          <button
            type="button"
            onClick={() => scrollToId("#amenities")}
            data-testid="jump-amenities"
            className="rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white transition-colors duration-300 hover:bg-[#F47221]"
          >
            Amenities
          </button>
          <button
            type="button"
            onClick={() => scrollToId("#uniform")}
            data-testid="jump-uniform"
            className="rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white transition-colors duration-300 hover:bg-[#F47221]"
          >
            Uniform
          </button>
        </motion.div>
      </PageHero>

      <section id="amenities" className="relative scroll-mt-20" data-testid="amenities-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28">
          <Reveal>
            <p className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.4em] text-[#F47221]">
              <span className="font-display text-2xl tracking-normal text-[#143A66]/25">
                01
              </span>
              Amenities
            </p>
            <h2 className="mt-5 font-display text-3xl font-semibold text-[#143A66] md:text-4xl">
              Spaces built for learning &amp; play
            </h2>
          </Reveal>
          <Reveal
            delay={0.1}
            data-lenis-prevent
            className="no-scrollbar -mx-6 mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 lg:mx-0 lg:grid lg:grid-cols-5 lg:overflow-visible lg:px-0"
          >
            {AMENITIES.map((a, i) => (
              <AmenityCard
                key={a.label}
                label={a.label}
                img={a.img}
                testId={`amenity-card-${i}`}
              />
            ))}
          </Reveal>
        </div>
      </section>

      <section id="uniform" className="relative scroll-mt-20" data-testid="uniform-section">
        <div className="mx-auto max-w-5xl px-6 pb-24 md:pb-32">
          <Reveal className="text-center">
            <p className="flex items-center justify-center gap-4 text-[11px] font-semibold uppercase tracking-[0.4em] text-[#F47221]">
              <span className="font-display text-2xl tracking-normal text-[#143A66]/25">
                02
              </span>
              Uniform
            </p>
            <h2 className="mt-5 font-display text-3xl font-semibold text-[#143A66] md:text-4xl">
              Uniform by class
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <UniformCarousel />
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
